import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { api } from '../services/api';

const WebAuthContext = createContext(null);

const TOKEN_KEY = 'web_token_v1';
const USER_KEY = 'web_user_v1';

function safeParse(json) {
  try { return JSON.parse(json); } catch { return null; }
}

function belongsToToken(token, user) {
  try {
    const payload = JSON.parse(atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
    return payload.type === 'web' && Number(payload.webUserId) > 0 && Number(payload.exp) * 1000 > Date.now() && String(payload.webUserId) === String(user?.id);
  } catch { return false; }
}

export function WebAuthProvider({ children }) {
  const [token, setToken] = useState('');
  const [user, setUser] = useState(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    const t = localStorage.getItem(TOKEN_KEY) || '';
    const u = safeParse(localStorage.getItem(USER_KEY) || 'null');
    if (t) setToken(t);
    if (u && belongsToToken(t, u)) setUser(u);
    setReady(true);
  }, []);

  useEffect(() => {
    const update = (event) => {
      if (event.key !== TOKEN_KEY && event.key !== USER_KEY) return;
      const nextToken = localStorage.getItem(TOKEN_KEY) || '';
      setToken(nextToken);
      const cached = safeParse(localStorage.getItem(USER_KEY) || 'null');
      setUser(nextToken && belongsToToken(nextToken, cached) ? cached : null);
    };
    window.addEventListener('storage', update);
    return () => window.removeEventListener('storage', update);
  }, []);

  // best-effort revalidate
  useEffect(() => {
    if (!ready || !token) return;
    let active = true;
    api.webMe(token)
      .then((res) => {
        if (!active) return;
        if (res?.data) {
          setUser(res.data);
          localStorage.setItem(USER_KEY, JSON.stringify(res.data));
        }
      })
      .catch((error) => {
        if (!active) return;
        if (![401, 403, 404].includes(error?.status)) return;
        // token invalid
        window.dispatchEvent(new Event('web-auth-logout'));
        setToken('');
        setUser(null);
        localStorage.removeItem(TOKEN_KEY);
        localStorage.removeItem(USER_KEY);
      });
    return () => { active = false; };
  }, [ready, token]);

  function loginWithToken(nextToken, nextUser) {
    setToken(nextToken);
    setUser(nextUser || null);
    localStorage.setItem(TOKEN_KEY, nextToken);
    if (nextUser) localStorage.setItem(USER_KEY, JSON.stringify(nextUser));
  }

  function updateUser(nextUser) {
    setUser(nextUser || null);
    if (nextUser) localStorage.setItem(USER_KEY, JSON.stringify(nextUser));
    else localStorage.removeItem(USER_KEY);
  }

  function logout() {
    window.dispatchEvent(new Event('web-auth-logout'));
    setToken('');
    setUser(null);
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
  }

  const value = useMemo(() => ({ token, user, ready, isAuthed: !!token && !!user && belongsToToken(token, user), loginWithToken, updateUser, logout }), [token, user, ready]);
  return <WebAuthContext.Provider value={value}>{children}</WebAuthContext.Provider>;
}

export function useWebAuth() {
  const ctx = useContext(WebAuthContext);
  if (!ctx) throw new Error('useWebAuth must be used within WebAuthProvider');
  return ctx;
}
